"use client";

import { useLocale } from "next-intl";
import { useRouter as useIntlRouter } from "@/i18n/navigation";
import { useMemo } from "react";
import type { Locale } from "@/i18n/routing";

/**
 * Back-compat wrapper that delegates to next-intl/navigation's useRouter.
 *
 * Pre-overhaul: this hook was a custom wrapper around next/navigation's
 * useRouter that auto-prepended the locale segment.
 *
 * Post-overhaul: next-intl/navigation's useRouter does the prepending
 * natively (with `localePrefix: 'always'`). We re-export it here so
 * existing callsites keep working unchanged. New code should
 * `import { useRouter } from "@/i18n/navigation"` directly.
 *
 * Phase 6 codemod will replace the imports + delete this file.
 */
export function useLocaleRouter() {
  const router = useIntlRouter();
  const locale = useLocale() as Locale;

  return useMemo(
    () => ({
      ...router,
      // Compat: existing callsites do `router.push(href)` expecting locale
      // to be auto-prepended. next-intl's router.push does this when called
      // from inside a NextIntlClientProvider context (which we mount in the
      // [locale]/layout.tsx). No extra wrapping needed.
      locale,
    }),
    [router, locale]
  );
}
